// AI-CONTEXT: See docs/features/materiaal-uitkomsten-tegels.md
import { useMemo, useState } from 'react'
import formatEuro from '../../helpers/formatEuro'
import formatCo2 from '../../helpers/materialsHelpers/formatCo2'
import buildInterventionCalculation from '../../helpers/materialsHelpers/buildInterventionCalculation'
import useMaterialOutcomesBreakdown from '../../hooks/portfolio/useMaterialOutcomesBreakdown'
import useInterventieLibrary from '../../hooks/useInterventieLibrary'
import PageHeader from '../../components/layout/PageHeader'
import Table from '../../components/common/table/Table'
import TableCell from '../../components/common/table/TableCell'
import MaterialCalculationModal from '../../components/dashboard/MaterialCalculationModal'
import MaterialUnknownModal from '../../components/dashboard/MaterialUnknownModal'
import Modal from '../../components/common/Modal'

const PROJECT_COLUMNS = [
    { key: 'name', label: 'PROJECT' },
    { key: 'households', label: 'HUISHOUDENS', searchable: false },
    { key: 'eur', label: 'ENERGIEREKENING', searchable: false },
    { key: 'co2', label: 'CO2', searchable: false },
    { key: 'unknown', label: 'NIET MEEGETELD', searchable: false },
]

const CODE_COLUMNS = [
    { key: 'name', label: 'MAATREGEL' },
    { key: 'units', label: 'EENHEDEN', searchable: false },
    { key: 'households', label: 'HUISHOUDENS', searchable: false },
    { key: 'eur', label: 'ENERGIEREKENING', searchable: false },
    { key: 'co2', label: 'CO2', searchable: false },
]

/** formatEuro maakt van null € 0,00; een onbekende is een streepje. */
const euro = (value) => (value === null || value === undefined ? '—' : formatEuro(value))

const aantal = (value) => new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 2 }).format(value || 0)

/**
 * Heeft dit project iets geregistreerd? Een project zonder één registratie is
 * geen rij met streepjes maar een telling onder de tabel.
 *
 * @param {{HouseholdCount: number, UnknownCount: number, Co2Kg: number|null}} outcomes
 * @returns {boolean}
 */
const hasRegistrations = (outcomes) =>
    (outcomes?.HouseholdCount || 0) > 0 || (outcomes?.UnknownCount || 0) > 0 || outcomes?.Co2Kg != null

/**
 * De besparingen van materialen en coachadviezen — op de energierekening en in
 * CO2 — per project, per maatregel, en per maatregel de rekenwijze van de
 * meetstandaard met de bron van elk getal.
 *
 * De totaalregel is hetzelfde getal als op de tegel: beide komen uit de
 * nachtrun, over dezelfde selectie projecten (getMaterialOutcomesBreakdown).
 * Een project zonder berekening telt niet als nul mee maar staat genoemd.
 * AI-CONTEXT: See docs/pitfalls.md#ui-aggregate-collapses-nothing-into-zero
 */
const MaterialOutcomesDetail = () => {
    const { data, loading, error } = useMaterialOutcomesBreakdown()
    const { document } = useInterventieLibrary()

    const [selectedId, setSelectedId] = useState(/** @type {string|null} */ (null))
    const [openCode, setOpenCode] = useState(/** @type {string|null} */ (null))
    // 'all' = de totaalregel, anders een CompagnyID. null = venster dicht.
    const [unknownScope, setUnknownScope] = useState(/** @type {string|null} */ (null))
    const [showEmptyProjects, setShowEmptyProjects] = useState(false)

    const allProjects = useMemo(() => (Array.isArray(data?.projects) ? data.projects : []), [data])
    const projects = useMemo(() => allProjects.filter((p) => hasRegistrations(p.Outcomes)), [allProjects])
    const emptyProjects = useMemo(() => allProjects.filter((p) => !hasRegistrations(p.Outcomes)), [allProjects])
    const zonderRegistraties = emptyProjects.length

    // Wat niet meetelt, voor het gekozen bereik. Bij 'alle projecten' staat de
    // projectnaam erbij; per project en materiaal, nooit per huishouden.
    const unknownRows = useMemo(() => {
        if (!unknownScope) return null
        const scoped = unknownScope === 'all' ? projects : projects.filter((p) => p.CompagnyID === unknownScope)
        return scoped.flatMap((p) => (Array.isArray(p.Outcomes.Unknown) ? p.Outcomes.Unknown : []).map((u) => ({
            ...u,
            project: p.Name,
            key: `${p.CompagnyID}|${u.MaterialID}|${u.Status}`,
        })))
    }, [unknownScope, projects])
    const unknownLabel = unknownScope === 'all'
        ? 'alle projecten'
        : (projects.find((p) => p.CompagnyID === unknownScope)?.Name || '')

    const selected = projects.find((p) => p.CompagnyID === selectedId) || null
    const scope = selected ? selected.Outcomes : data?.total
    const scopeLabel = selected ? selected.Name : 'alle projecten'
    const codes = useMemo(() => (Array.isArray(scope?.PerCode) ? scope.PerCode : []), [scope])

    const unitOf = (code) => (document?.interventies || []).find((i) => i?.id === code)?.eenheid || ''

    const calculation = useMemo(
        () => (openCode ? buildInterventionCalculation(document, openCode) : null),
        [document, openCode]
    )

    const pending = Array.isArray(data?.pending) ? data.pending : []
    const berekendOp = data?.computedAt
        ? new Date(data.computedAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })
        : ''

    return (
        <div className='main'>
            <div className='main-container'>
                <PageHeader heading='Besparingen met materialen' subHeading='' description='' AI={false} />

                {error ? (
                    <p className='participation-value-error'>
                        We konden de besparingen niet ophalen. Ververs de pagina of probeer het later opnieuw.
                    </p>
                ) : loading ? (
                    <p className='participation-value-note'>De besparingen worden opgehaald…</p>
                ) : (
                    <>
                        <p className='participation-value-note'>
                            Per jaar, berekend volgens de meetstandaard milieu en circulariteit.
                            {berekendOp && ` Bijgewerkt op ${berekendOp}; de getallen worden elke nacht opnieuw berekend.`}
                        </p>

                        {pending.length > 0 &&
                            <p className='participation-value-note'>
                                {`Nog niet berekend, en dus niet in het totaal: ${pending.join(', ')}.`}
                            </p>
                        }

                        {projects.length === 0 ? (
                            pending.length === 0 && (
                                <p className='participation-value-note'>Er zijn nog geen materialen of adviezen geregistreerd.</p>
                            )
                        ) : (
                            <>
                                <div className='dashboard-container'>
                                    <Table
                                        ariaLabel='Per project'
                                        columns={PROJECT_COLUMNS}
                                        items={projects}
                                        getItemKey={(project) => project.CompagnyID}
                                        searchAccessor={(project) => project.Name || ''}
                                        searchPlaceholder='Zoeken op project'
                                        emptyMessage='Geen projecten gevonden.'
                                        getRowClassName={(project) => (project.CompagnyID === selectedId ? 'material-outcomes-row--selected' : '')}
                                        renderRow={(project) => (
                                            <>
                                                <TableCell>
                                                    <button
                                                        type='button'
                                                        className='material-outcomes-link'
                                                        aria-pressed={project.CompagnyID === selectedId}
                                                        onClick={() => setSelectedId(project.CompagnyID === selectedId ? null : project.CompagnyID)}
                                                    >
                                                        {project.Name || '—'}
                                                    </button>
                                                </TableCell>
                                                <TableCell>{aantal(project.Outcomes.HouseholdCount)}</TableCell>
                                                <TableCell>{euro(project.Outcomes.PrivateEur)}</TableCell>
                                                <TableCell>{formatCo2(project.Outcomes.Co2Kg)}</TableCell>
                                                <TableCell>
                                                    {project.Outcomes.UnknownCount > 0 ? (
                                                        <button
                                                            type='button'
                                                            className='material-outcomes-link'
                                                            aria-label={`${aantal(project.Outcomes.UnknownCount)} niet meegeteld bij ${project.Name}`}
                                                            onClick={() => setUnknownScope(project.CompagnyID)}
                                                        >
                                                            {aantal(project.Outcomes.UnknownCount)}
                                                        </button>
                                                    ) : aantal(project.Outcomes.UnknownCount)}
                                                </TableCell>
                                            </>
                                        )}
                                        footerRow={data?.total ? (
                                            <>
                                                <TableCell>Totaal</TableCell>
                                                <TableCell>{aantal(data.total.HouseholdCount)}</TableCell>
                                                <TableCell>{euro(data.total.PrivateEur)}</TableCell>
                                                <TableCell>{formatCo2(data.total.Co2Kg)}</TableCell>
                                                <TableCell>
                                                    {data.total.UnknownCount > 0 ? (
                                                        <button
                                                            type='button'
                                                            className='material-outcomes-link'
                                                            aria-label={`${aantal(data.total.UnknownCount)} niet meegeteld bij alle projecten`}
                                                            onClick={() => setUnknownScope('all')}
                                                        >
                                                            {aantal(data.total.UnknownCount)}
                                                        </button>
                                                    ) : aantal(data.total.UnknownCount)}
                                                </TableCell>
                                            </>
                                        ) : null}
                                    />
                                    {zonderRegistraties > 0 &&
                                        <p className='participation-value-note'>
                                            <button type='button' className='material-outcomes-link' onClick={() => setShowEmptyProjects(true)}>
                                                {`${zonderRegistraties} ${zonderRegistraties === 1 ? 'project registreert' : 'projecten registreren'} nog geen materialen.`}
                                            </button>
                                        </p>
                                    }
                                </div>

                                <div className='dashboard-container'>
                                    <h2 className='material-outcomes-heading'>{`Per maatregel — ${scopeLabel}`}</h2>
                                    {selected &&
                                        <button type='button' className='material-outcomes-link' onClick={() => setSelectedId(null)}>
                                            Toon alle projecten
                                        </button>
                                    }
                                    <Table
                                        ariaLabel='Per maatregel'
                                        columns={CODE_COLUMNS}
                                        items={codes}
                                        getItemKey={(row) => row.Code}
                                        showSearch={false}
                                        emptyMessage='Geen maatregelen met een berekende besparing.'
                                        renderRow={(row) => (
                                            <>
                                                <TableCell>
                                                    <button type='button' className='material-outcomes-link' onClick={() => setOpenCode(row.Code)}>
                                                        {row.Name}
                                                    </button>
                                                </TableCell>
                                                <TableCell>{unitOf(row.Code) ? `${aantal(row.Units)} (${unitOf(row.Code)})` : aantal(row.Units)}</TableCell>
                                                <TableCell>{aantal(row.HouseholdCount)}</TableCell>
                                                <TableCell>{euro(row.PrivateEur)}</TableCell>
                                                <TableCell>{formatCo2(row.Co2Kg)}</TableCell>
                                            </>
                                        )}
                                    />
                                    <p className='participation-value-note'>Klik op een maatregel voor de rekenwijze en de bronnen.</p>
                                </div>
                            </>
                        )}
                    </>
                )}

                <MaterialUnknownModal
                    rows={unknownRows}
                    scopeLabel={unknownLabel}
                    withProject={unknownScope === 'all'}
                    onClose={() => setUnknownScope(null)}
                />

                {showEmptyProjects &&
                    <Modal
                        modalOpen
                        setModalOpen={() => setShowEmptyProjects(false)}
                        pageHeader='Projecten zonder registraties'
                        description='Deze projecten hebben nog geen materialen of adviezen bij huishoudens geregistreerd. Ze tellen dus niet mee in de besparingen.'
                        content={(
                            <ul className='material-outcomes-empty-list'>
                                {emptyProjects.map((p) => <li key={p.CompagnyID}>{p.Name || p.CompagnyID}</li>)}
                            </ul>
                        )}
                        hideConfirm
                        cancelLabel='Sluiten'
                    />
                }

                <MaterialCalculationModal
                    calculation={calculation}
                    totals={codes.find((c) => c.Code === openCode) || null}
                    scopeLabel={scopeLabel}
                    onClose={() => setOpenCode(null)}
                />
            </div>
        </div>
    )
}

export default MaterialOutcomesDetail
